/**
 * LogPage - Real-time log viewer for debugging
 *
 * Shows all app events, import progress, errors, and more.
 * Auto-updates as new logs come in.
 *
 * SECURITY: This page is restricted to owner only (nova223@gmail.com)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { devlog, LogEntry, LogLevel, LogCategory } from '@services/devlog';
import { Card, Button } from '@/components/shared';
import { useOwnerAccess } from '@/hooks/useOwnerAccess';
import './LogPage.css';

export default function LogPage() {
  const { isOwner, isLoading: authLoading, userEmail } = useOwnerAccess();

  // SECURITY: Redirect non-owners
  if (authLoading) {
    return <div className="loading">Checking access...</div>;
  }

  if (!isOwner) {
    console.warn('[LOG] Unauthorized access attempt:', userEmail);
    return <Navigate to="/" replace />;
  }

  return <LogPageContent />;
}

function LogPageContent() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filterLevel, setFilterLevel] = useState<LogLevel | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<LogCategory | 'all'>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Load initial logs and subscribe to updates
  useEffect(() => {
    setLogs(devlog.getAll());

    const unsubscribe = devlog.subscribe((entry) => {
      setLogs((prev) => [...prev.slice(-999), entry]); // Keep last 1000
    });

    return unsubscribe;
  }, []);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    if (filterLevel !== 'all' && log.level !== filterLevel) return false;
    if (filterCategory !== 'all' && log.category !== filterCategory) return false;
    return true;
  });

  // Clear logs
  const handleClear = useCallback(() => {
    devlog.clear();
    setLogs([]);
  }, []);

  // Export logs
  const handleExport = useCallback(() => {
    const text = devlog.exportText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ninjacash-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Get stats
  const stats = devlog.getStats();

  return (
    <div className="log-page">
      {/* Header */}
      <header className="log-header">
        <h1 className="log-title">Logs</h1>
        <div className="log-actions">
          <Button variant="ghost" onClick={handleExport}>
            Export
          </Button>
          <Button variant="ghost" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="log-stats">
        <span className="stat-total">{stats.total} entries</span>
        <span className="stat-level stat-error">{stats.byLevel.error} errors</span>
        <span className="stat-level stat-warn">{stats.byLevel.warn} warnings</span>
        <span className="stat-level stat-info">{stats.byLevel.info} info</span>
        <span className="stat-level stat-debug">{stats.byLevel.debug} debug</span>
      </div>

      {/* Filters */}
      <Card className="log-filters">
        <div className="filter-group">
          <label>Level:</label>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value as LogLevel | 'all')}
          >
            <option value="all">All</option>
            <option value="error">Error</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Category:</label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as LogCategory | 'all')}
          >
            <option value="all">All</option>
            <option value="import">Import</option>
            <option value="db">Database</option>
            <option value="sync">Sync</option>
            <option value="ui">UI</option>
            <option value="app">App</option>
            <option value="theme">Theme</option>
            <option value="nav">Navigation</option>
          </select>
        </div>

        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>
        </div>

        <div className="filter-result">
          Showing {filteredLogs.length} of {logs.length}
        </div>
      </Card>

      {/* Log Entries */}
      <div className="log-container" ref={logContainerRef}>
        {filteredLogs.length === 0 ? (
          <div className="log-empty">
            <p>No logs to display.</p>
            <p className="log-empty-hint">Import a CSV file or navigate around to generate logs.</p>
          </div>
        ) : (
          filteredLogs.map((entry) => (
            <div key={entry.id} className={`log-entry log-${entry.level}`}>
              <span className="log-time">
                {entry.timestamp.toLocaleTimeString('en-US', { hour12: false })}
              </span>
              <span className={`log-level level-${entry.level}`}>
                {entry.level.toUpperCase()}
              </span>
              <span className="log-category">{entry.category}</span>
              <span className="log-message">{entry.message}</span>
              {entry.data !== undefined && (
                <span className="log-data">
                  {typeof entry.data === 'object'
                    ? JSON.stringify(entry.data)
                    : String(entry.data)}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
